import {useTransition} from '@yandex-delivery/blocks'
import {useFormContext} from '@yandex-delivery/nlf-form/v2'
import {useCallback, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import {type Poll, type PollQuestion, type PollQuestionAnswer, fetchSharedRoutePoll} from 'src/api'

import type {RouterParams} from '../../types'
import type {AnswerProps} from './Answer'
import type {FormType} from './Form.types'
import {formToQuestionChoice, getNewValues} from './Poll.utils'
import {getSubPollQuestions} from './utils'

type UsePoll = Pick<AnswerProps, 'handleAnswerClick'> & {
  isPollCompleted: boolean
  questions: PollQuestion[]
}

export const usePoll = (poll: Poll): UsePoll => {
  const location = useLocation<RouterParams>()
  const sharingKey = location.hash.substring(1)
  const {values: form, setFieldValue} = useFormContext<FormType>()
  const formValues = useMemo(() => form?.poll || {}, [form])
  const [, startTransition] = useTransition(undefined, {key: 'sharing-route/poll'})
  const questions = useMemo(() => getSubPollQuestions(poll, formValues), [poll, formValues])

  const handleAnswerClick = useCallback(
    (question: PollQuestion, answer: PollQuestionAnswer) => {
      const currentAnswerValues = formValues[question.id]
      const newValues = getNewValues(currentAnswerValues, answer.id, question.choices_type)
      setFieldValue('poll', {...formValues, [question.id]: newValues})
      startTransition(() =>
        fetchSharedRoutePoll(
          sharingKey,
          formToQuestionChoice(poll.subpolls, {...formValues, [question.id]: newValues}),
        ),
      )
    },
    [formValues, poll.subpolls, setFieldValue, sharingKey, startTransition],
  )

  const isPollCompleted = !!Object.values(formValues).length

  return {handleAnswerClick, isPollCompleted, questions}
}
